/**
 * variantAxes — groups a product's flat ``variants`` list into
 * axis-based buckets so the customer PDP + listing pickers can render
 * a proper multi-axis selector (Color × Size, Amazon/Flipkart-style).
 *
 * Two data models are supported without any merchant-side migration:
 *
 * 1) **Flat + typed** (current): each variant is a standalone SKU with
 *    ``type`` = COLOR / SIZE / CUSTOM. We group by ``type`` so each
 *    type becomes an axis row. Selecting one row's chip picks that
 *    SKU; combinations across rows aren't defined so only the
 *    most-recently-touched axis's chip stays highlighted at a time.
 *
 * 2) **Matrix (future-proof)**: when a variant carries
 *    ``option_values: {"Color":"Red","Size":"L"}`` we treat the keys
 *    as axis names and the values as chip labels. Multiple axes can
 *    be selected simultaneously; ``resolveVariant`` returns the SKU
 *    whose option_values match the selection.
 *
 * ``enabledValues(axisName, selection)`` returns the Set of values on
 * ``axisName`` that would still resolve to a real (enabled, in-stock)
 * SKU given the partial selection on other axes — that's how the "only
 * valid combinations" gating works.
 */

const TYPE_LABELS = { COLOR: "Color", SIZE: "Size", CUSTOM: "Option" };

function axisLabel(name) {
    return TYPE_LABELS[name] || name;
}

/**
 * Detects if the product's variants encode a multi-axis matrix. When
 * true, we use ``option_values``. When false, we fall back to grouping
 * by the legacy ``type`` field.
 */
export function hasMatrix(variants = []) {
    return variants.some((v) => v?.option_values && Object.keys(v.option_values).length > 0);
}

/**
 * Build the list of axes for a product. Each axis is
 * ``{ key, label, values: [{value, label, sample_variant, color_hex?}] }``
 * where ``sample_variant`` is any variant carrying that value —
 * useful for pulling a price / color chip when rendering the option.
 */
export function buildAxes(variants = []) {
    const enabled = variants.filter((v) => v?.enabled !== false);
    if (enabled.length === 0) return [];

    if (hasMatrix(enabled)) {
        // Discover axis order from the FIRST variant that has option_values,
        // then walk every variant to collect the distinct values per axis.
        const order = [];
        const seenAxes = new Set();
        for (const v of enabled) {
            for (const k of Object.keys(v.option_values || {})) {
                if (!seenAxes.has(k)) { order.push(k); seenAxes.add(k); }
            }
        }
        return order.map((axisKey) => {
            const seen = new Map();  // value → sample_variant
            for (const v of enabled) {
                const val = v.option_values?.[axisKey];
                if (val && !seen.has(val)) seen.set(val, v);
            }
            return {
                key: axisKey,
                label: axisLabel(axisKey),
                values: [...seen.entries()].map(([value, sample]) => ({
                    value, label: value, sample_variant: sample,
                    color_hex: sample.color_hex || null,
                })),
            };
        });
    }

    // Legacy flat model — group by ``type``. Each variant IS its own
    // value on that axis (there's no cross-product), so ``label`` is
    // the variant's own label and ``sample_variant`` is the variant.
    const grouped = new Map();
    for (const v of enabled) {
        const k = v.type || "CUSTOM";
        if (!grouped.has(k)) grouped.set(k, []);
        grouped.get(k).push(v);
    }
    // Preserve type discovery order: COLOR / SIZE first if present,
    // then whichever custom axes the merchant added.
    const preferred = ["COLOR", "SIZE"];
    const keys = [
        ...preferred.filter((p) => grouped.has(p)),
        ...[...grouped.keys()].filter((k) => !preferred.includes(k)),
    ];
    return keys.map((axisKey) => {
        const arr = [...grouped.get(axisKey)].sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        );
        return {
            key: axisKey,
            label: axisLabel(axisKey),
            values: arr.map((v) => ({
                value: v.id,
                label: v.label,
                sample_variant: v,
                color_hex: v.color_hex || null,
            })),
        };
    });
}

/**
 * Resolve the current selection to a concrete variant.
 *
 *   selection = { COLOR: "Red", SIZE: "L" }         // matrix mode
 *   selection = { COLOR: "<variant-id>" }           // flat mode
 *
 * Returns the matching variant or ``null`` when the selection is
 * incomplete or invalid.
 */
export function resolveVariant(variants, axes, selection) {
    if (!variants?.length || !axes?.length) return null;
    const enabled = variants.filter((v) => v?.enabled !== false);

    if (hasMatrix(enabled)) {
        // All axes must be selected. Find the variant whose
        // option_values match every axis in the selection.
        for (const axis of axes) {
            if (!selection[axis.key]) return null;
        }
        return enabled.find((v) => {
            for (const axis of axes) {
                if ((v.option_values || {})[axis.key] !== selection[axis.key]) return false;
            }
            return true;
        }) || null;
    }

    // Flat mode — selection value IS the variant id. Only one axis
    // can be meaningfully selected at once (the last one the user
    // touched). Return that variant.
    for (const axis of axes) {
        const val = selection[axis.key];
        if (val) return enabled.find((v) => v.id === val) || null;
    }
    return null;
}

/**
 * Given the current partial selection, return the Set of ``values``
 * on ``axisName`` that would still resolve to at least one enabled
 * SKU. Used to grey-out chips that would form an invalid combination.
 *
 * In flat mode every value is always "enabled" (there are no
 * combinations to invalidate); in matrix mode we filter to only the
 * combinations that exist.
 */
export function enabledValuesFor(variants, axisName, selection) {
    const enabled = variants.filter((v) => v?.enabled !== false);
    if (!hasMatrix(enabled)) {
        return new Set(enabled.map((v) => v.id));
    }
    const out = new Set();
    for (const v of enabled) {
        let match = true;
        for (const [k, val] of Object.entries(selection || {})) {
            if (k === axisName) continue;
            if ((v.option_values || {})[k] !== val) { match = false; break; }
        }
        if (match) out.add((v.option_values || {})[axisName]);
    }
    return out;
}

/**
 * Pick a sensible default selection — the ``is_default`` variant's
 * option_values (matrix), or the first variant (flat).
 */
export function defaultSelection(variants, axes) {
    const enabled = variants.filter((v) => v?.enabled !== false);
    if (!enabled.length || !axes.length) return {};
    const def = enabled.find((v) => v.is_default) || enabled[0];
    if (hasMatrix(enabled)) return { ...(def.option_values || {}) };
    // Flat — populate ONLY the axis this variant lives on.
    const axisKey = def.type || "CUSTOM";
    return { [axisKey]: def.id };
}

/**
 * Given a change to ``changedAxis``, return a new selection that
 * (a) applies the change, and (b) walks the other axes and re-selects
 * them to values that still resolve to a real SKU. This is how Amazon
 * / Flipkart handle "tapping a color whose current size doesn't exist"
 * — the color updates and the size auto-repairs to the first valid
 * size for that color.
 *
 * In flat mode, since each variant is its own single-axis SKU, we
 * simply clear the other axes so exactly one value stays selected.
 */
export function applyAxisChange(variants, axes, prevSelection, changedAxis, newValue) {
    const enabled = variants.filter((v) => v?.enabled !== false);
    // Flat mode → one-axis selection at a time.
    if (!hasMatrix(enabled)) {
        return { [changedAxis]: newValue };
    }
    const next = { ...prevSelection, [changedAxis]: newValue };
    // Repair every other axis whose current value is now invalid.
    for (const axis of axes) {
        if (axis.key === changedAxis) continue;
        const partial = { ...next };
        delete partial[axis.key];                       // don't self-constrain
        const validSet = enabledValuesFor(enabled, axis.key, partial);
        const cur = next[axis.key];
        if (!cur || !validSet.has(cur)) {
            // Prefer the axis's first value that IS valid.
            const firstValid = axis.values.find((o) => validSet.has(o.value));
            if (firstValid) next[axis.key] = firstValid.value;
            else delete next[axis.key];                 // shouldn't happen with real data
        }
    }
    return next;
}
